import mixins from 'vue-typed-mixins';
import { PropType } from 'vue';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { ErrorResponse } from '@codequality/vue-cli-base';

import { E償却方法, Eフェーズ種別 } from '@/openapi/enum';
import { YearValue, データApi, 勘定科目ListItemResponse } from '@/openapi/user';

import C固定資産科目Select from '@/components/勘定科目/固定資産科目Select/固定資産科目Select.vue';
import InputableText from '@/components/inputableText/InputableText.vue';

import AlertMixin from '@/mixins/AlertMixin';
import ElDatePickerMixin from '@/mixins/ElDatePickerMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import フェーズ種別Mixin from '@/entries/user/mixins/フェーズ種別Mixin';
import 償却保存Mixin from '@/entries/user/mixins/償却保存Mixin';
import { InputableTextMixin } from '@/components/inputableText/InputableText';
import InputableTextBaseMixin from '@/entries/user/mixins/InputableTextBaseMixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import 償却Service from '@/classes/service/分析計画/償却Service';
import { 償却EditItem, 償却ListItem, 償却決算期 } from '@/classes/service/分析計画/償却Type';

export default mixins(
  AlertMixin,
  ElDatePickerMixin,
  計画書Mixin,
  フェーズ種別Mixin,
  償却保存Mixin,
  InputableTextMixin,
  InputableTextBaseMixin
).extend({
  name: 'C償却EditRow',

  components: {
    C固定資産科目Select,
    InputableText,
  },

  props: {
    /** 編集中の一覧データ */
    listItem: {
      type: Object as PropType<償却ListItem | null>,
      default: null,
    },
    /** 償却決算期 */
    決算期s: {
      type: Array as PropType<償却決算期[]>,
      default: () => [],
    },
    service: {
      type: Object as PropType<償却Service>,
    },
    /** 簡易表示 */
    isSimple: {
      type: Boolean,
      default: false,
    },
    /** 固定資産科目Select用 */
    勘定科目s: {
      type: Array as PropType<勘定科目ListItemResponse[]>,
      default: () => [],
    },
    /** 番号最大値 */
    maxNo: {
      type: Number,
      default: 0,
    },
    /** 新規/更新 */
    isNew: {
      type: Boolean,
      default: true,
    },
  },

  data(): {
    e償却方法: typeof E償却方法;
    api: データApi;
    editItem: 償却EditItem | null;
    input減価償却s: (YearValue | null)[];
  } {
    return {
      e償却方法: E償却方法,
      api: UserApiUtility.ogApi(データApi),
      editItem: null,
      input減価償却s: [],
    };
  },

  watch: {
    // 選択中データに変更があった場合に入力エリアの値を変更する
    listItem(): void {
      this.$_loadToEditItem();
    },
    maxNo(): void {
      if (this.editItem === null) return;
      this.editItem.番号 = this.maxNo + 1;
    },
  },

  computed: {
    is分析(): boolean {
      return this.eフェーズ種別 === Eフェーズ種別.分析フェーズ;
    },
    saveButtonLabel(): string {
      return this.isNew ? '追加' : '更新';
    },
    canEditリース償却(): boolean {
      if (this.editItem === null) return false;
      return this.editItem.償却方法 === E償却方法.リース期間定額法;
    },
    editingRowCss(): string {
      return this.isNew ? '' : 'editingRow';
    },
  },

  created(): void {
    this.$_loadToEditItem();
  },

  methods: {
    recalculate() {
      if (this.editItem === null) return;
      const v再計算減価償却s = this.service.create減価償却s(this.editItem).減価償却s;
      this.input減価償却s.forEach((inputItem) => {
        if (inputItem === null) return;
        inputItem.value = v再計算減価償却s.find((r) => r.year === inputItem.year)!.value;
      });
    },

    async save(): Promise<void> {
      if (this.editItem === null) return;
      const { データpk, 番号, フェーズ種別, ...cデータ償却 } = this.editItem;

      // 画面表示用にeditItemには、償却方法がリース償却でなくても'リース償却'プロパティが存在しているが、DB登録時は必要ないので削除する
      if (cデータ償却.償却方法 !== E償却方法.リース期間定額法) {
        delete cデータ償却.リース償却;
      }

      // 更新の場合、計算減価償却Mapのvalueにinput減価償却sの値をマージしてcデータ償却.減価償却sにセットする
      if (!this.isNew) {
        const 年次計算減価償却Map = this.listItem!.年次計算減価償却Map;
        cデータ償却.減価償却s = this.create減価償却s(年次計算減価償却Map, this.input減価償却s);
      }

      const request = this.create償却データRequest(番号!, this.eフェーズ種別, cデータ償却);
      const response: AxiosResponse<void | ErrorResponse> = this.isNew
        ? await this.api.createデータ(this.計画書.pk, request)
        : await this.api.updateデータ(データpk!, request);

      if (this.isValidationErrorResponse(response)) return;
      this.clearForm();
      this.$emit('load');
    },

    clearForm(): void {
      this.$_clearForm();
      this.$emit('clear');
    },

    $_clearForm(): void {
      this.editItem = this.$_initialEditItem();
      this.input減価償却s = [];
    },

    $_initialEditItem(): 償却EditItem {
      return {
        データpk: 0,
        番号: this.maxNo + 1,
        名称: '',
        償却方法: E償却方法.定額法,
        事業供用日: '',
        取得価額: 0,
        リース償却: {
          月数: 0,
          残価保証額: 0,
        },
      };
    },

    $_loadToEditItem(): void {
      if (this.listItem === null) {
        this.$_clearForm();
      } else {
        // 更新対象データの取得
        const {
          科目名,
          償却率,
          定率法償却,
          再計算前期末簿価,
          償却不足額,
          部門名,
          年次計算減価償却Map,
          ...v償却EditItem
        } = this.listItem;

        // 編集用オブジェクトに必要な値のみコピー
        this.editItem = _.cloneDeep(v償却EditItem);

        // 減価償却入力用配列に計算減価償却の値をコピー
        this.input減価償却s = this.決算期s.map((決算期) => {
          const 計算減価償却 = 年次計算減価償却Map.get(決算期.year);
          if (計算減価償却 === undefined) return null;
          return {
            year: 決算期.year,
            value: 計算減価償却.減価償却費,
          };
        });
      }
    },
    changeCell(value: string | number, row: YearValue): void {
      this.$set(row, 'value', Number(value));
    },
    change前期末簿価(value: string | number, row: 償却EditItem | null, key: string): void {
      if (row != null) this.$set(row, key, Number(value));
    },
  },
});
